import { forwardRef } from 'react';

import { cva, type VariantProps } from 'cva';

import styles from './grid.module.css';

const gridVariants = cva({
  base: styles.grid,
  variants: {
    gap: {
      s: styles.gap_s,
      m: styles.gap_m,
      l: styles.gap_l,
      xl: styles.gap_xl,
      '2xl': styles.gap_2xl,
      '3xl': styles.gap_3xl,
      '4xl': styles.gap_4xl,
    },
  },
  defaultVariants: {
    gap: 'm',
  },
});

export type GridProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof gridVariants> & {
    templateColumns?: string;
    templateRows?: string;
    templateAreas?: string;
  };

/**
 * Grid is a layout component that arranges its children in a grid, using CSS
 * grid.
 *
 * It allows you to control the gap between children, the template columns and
 * rows, and the template areas.
 *
 * You can use the `gridVariants` helper to create a custom component with the
 * props and styling from Grid.
 *
 * ```tsx
 * <Grid templateColumns="repeat(2, 1fr)" templateRows="60px 1fr" gap="m">
 *  <Sheet>1</Sheet>
 *  <Sheet>2</Sheet>
 *  <Sheet>3</Sheet>
 *  <Sheet>4</Sheet>
 * </Grid>
 * ```
 */
export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      gap,
      className,
      templateColumns,
      templateRows,
      templateAreas,
      style,
      ...props
    },
    ref,
  ) => {
    const resultStyles = {
      ...style,
      '--grid-template-columns': templateColumns,
      '--grid-template-rows': templateRows,
      '--grid-template-areas': templateAreas,
    };
    const classes = gridVariants({
      gap,
      className,
    });

    return (
      <div ref={ref} {...props} style={resultStyles} className={classes} />
    );
  },
);
